/**
 * A workbook kept sheet by sheet, shown as one upload with its sheets beneath it (D-82, D-93,
 * W3-17 (4d)).
 *
 * The service keeps such a workbook as a parent holding no lines and one document per sheet. Listed
 * flat, eight rows of one file said nothing about belonging together, and the parent read only
 * "Split". These helpers put each sheet under its workbook and say, in one line, where the sheets
 * stand.
 */

interface Doc {
  id: string
  parent_id?: string | null
  sheet_name?: string | null
  held?: string
  stage?: string | null
  source_filename?: string | null
}

/** An upload, and the sheets kept beneath it (none for any other upload). */
export interface Upload<T extends Doc> {
  doc: T
  sheets: T[]
}

/** Each upload in the order given, a workbook's sheets beneath it rather than beside it. */
export function nestWorkbooks<T extends Doc>(rows: T[]): Upload<T>[] {
  const ids = new Set(rows.map((r) => r.id))
  const under = new Map<string, T[]>()
  for (const row of rows) {
    if (row.sheet_name && row.parent_id) {
      under.set(row.parent_id, [...(under.get(row.parent_id) ?? []), row])
    }
  }
  return rows
    .filter((row) => !(row.sheet_name && row.parent_id && ids.has(row.parent_id)))
    .map((doc) => ({ doc, sheets: under.get(doc.id) ?? [] }))
}

/** A sheet's documents less the ones a later reading replaced: one per sheet, as it stands. */
export function currentSheets<T extends Doc>(sheets: T[]): T[] {
  return sheets.filter((s) => s.held !== 'superseded')
}

/** The sheets a workbook's reject takes (D-93): those still waiting for their columns. */
export function waitingForColumns<T extends Doc>(sheets: T[]): T[] {
  return currentSheets(sheets).filter((s) => s.held === 'needs_mapping')
}

/**
 * What a workbook's reject asks, and its two answers, agreeing with the count (N34): one sheet is
 * "it", never "them".
 */
export function rejectWaitingWords(n: number): { question: string; reject: string; keep: string } {
  return {
    question:
      `Reject the ${n} sheet${n === 1 ? '' : 's'} waiting for columns? ` +
      'A sheet that was read is not touched.',
    reject: n === 1 ? 'Reject it' : 'Reject them',
    keep: n === 1 ? 'Keep it' : 'Keep them',
  }
}

/** Where a workbook's sheets stand, in one line: "3 sheets: 1 read, 2 waiting for columns". */
export function workbookState(sheets: Doc[]): string {
  const now = currentSheets(sheets)
  const counts: Array<[string, number]> = [
    ['read', now.filter((s) => !s.held).length],
    ['waiting for columns', now.filter((s) => s.held === 'needs_mapping').length],
    ['rejected', now.filter((s) => s.held === 'rejected').length],
    ['held', now.filter((s) => s.held && s.held !== 'needs_mapping' && s.held !== 'rejected').length],
  ]
  const parts = counts.filter(([, n]) => n > 0).map(([words, n]) => `${n} ${words}`)
  return `${now.length} sheet${now.length === 1 ? '' : 's'}${parts.length ? `: ${parts.join(', ')}` : ''}`
}

/** Raw Data's queue in groups: a workbook's sheets together under its name, other files alone. */
export function queueGroups<T extends Doc>(
  queue: T[],
): Array<{ workbook: string | null; key: string; docs: T[] }> {
  const groups: Array<{ workbook: string | null; key: string; docs: T[] }> = []
  const byParent = new Map<string, { workbook: string | null; key: string; docs: T[] }>()
  for (const doc of queue) {
    if (doc.sheet_name && doc.parent_id) {
      const found = byParent.get(doc.parent_id)
      if (found) {
        found.docs.push(doc)
        continue
      }
      const group = { workbook: doc.source_filename ?? 'A workbook', key: doc.parent_id, docs: [doc] }
      byParent.set(doc.parent_id, group)
      groups.push(group)
      continue
    }
    groups.push({ workbook: null, key: doc.id, docs: [doc] })
  }
  return groups
}
